import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { LDateTime } from '@app/components/date/LDateTime';
import { useToast } from '@app/core/notifications';
import { socialExport } from '@mi-front/api-clients';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Button, Stack } from '@mui/material';
import AlertTitle from '@mui/material/AlertTitle';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

export function SocialUploadScheduledStatus({
  upload,
  scheduledData,
}: {
  upload: socialExport.UploadDto;
  scheduledData: socialExport.UploadScheduledDataDto;
}) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = socialExport.useDeleteUpload({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExport.getUploadByKeyQueryKey(upload.key, upload.platform),
        });
        notifySuccess(
          $t({
            id: 'fragments.socialExport.publicationDeleted',
            defaultMessage: 'Publicación eliminada',
          }),
        );
      },
      onError: (error: Error) => {
        notifyError(
          error?.message ||
            $t({
              id: 'fragments.socialExport.errorDeletingPublication',
              defaultMessage: 'Ocurrió un error al eliminar la publicación',
            }),
        );
      },
    },
  });

  const promoteMutation = socialExport.usePromoteUpload({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExport.getUploadByKeyQueryKey(upload.key, upload.platform),
        });
        notifySuccess(
          $t({
            id: 'fragments.socialExport.publicationStarted',
            defaultMessage: 'Publicación iniciada',
          }),
        );
      },
      onError: (error: Error) => {
        notifyError(
          error?.message ||
            $t({
              id: 'fragments.socialExport.errorPublishingNow',
              defaultMessage: 'Ocurrió un error al publicar inmediatamente',
            }),
        );
      },
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: upload.id });
  };

  const handlePromote = async () => {
    await promoteMutation.mutateAsync({ id: upload.id });
  };

  return (
    <Alert severity="info" icon={<ScheduleSendIcon />}>
      <AlertTitle>
        <FormattedMessage
          id="fragments.socialExport.scheduledAlertTitle"
          defaultMessage="Publicación programada"
        />
      </AlertTitle>
      <Box mt={1}>
        <FormattedMessage
          id="fragments.socialExport.videoWillPublishAt"
          defaultMessage="El video se publicará el:"
        />{' '}
        <strong>
          <LDateTime value={scheduledData.publishAt} showRelative="inline" />
        </strong>
      </Box>
      <Box mt={2}>
        <Stack direction="row" spacing={2}>
          <ConfirmationAction
            title={$t({
              id: 'fragments.socialExport.publishNowConfirmTitle',
              defaultMessage: 'Publicar ahora',
            })}
            message={
              <FormattedMessage
                id="fragments.socialExport.publishNowConfirmMessage"
                defaultMessage="¿Estás seguro de que deseas publicar este video ahora en lugar de esperar a la fecha programada?"
              />
            }
            confirmLabel={$t({
              id: 'fragments.socialExport.publishNow',
              defaultMessage: 'Publicar ahora',
            })}
            cancelLabel={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            confirmColor="primary"
            onConfirm={handlePromote}
          >
            {({ onClick }) => (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={onClick}
              >
                <FormattedMessage
                  id="fragments.socialExport.publishNow"
                  defaultMessage="Publicar ahora"
                />
              </Button>
            )}
          </ConfirmationAction>
          <ConfirmationAction
            title={$t({
              id: 'fragments.socialExport.deleteScheduledPublishTitle',
              defaultMessage: 'Eliminar publicación programada',
            })}
            message={
              <FormattedMessage
                id="fragments.socialExport.deleteScheduledPublishMessage"
                defaultMessage="¿Estás seguro de que deseas eliminar esta publicación programada? Esta acción no se puede deshacer."
              />
            }
            confirmLabel={$t({
              id: 'common.delete',
              defaultMessage: 'Eliminar',
            })}
            cancelLabel={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            confirmColor="error"
            onConfirm={handleDelete}
          >
            {({ onClick }) => (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onClick}
              >
                <FormattedMessage id="common.delete" defaultMessage="Eliminar" />
              </Button>
            )}
          </ConfirmationAction>
        </Stack>
      </Box>
    </Alert>
  );
}
